import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  api,
  assetUrls,
  formatBytes,
  formatDuration,
  type Asset,
  type Facets,
  type Folder,
  type SearchParams,
} from '../lib/api.js';
import {
  browseKey,
  readBrowseSnapshot,
  saveBrowseSnapshot,
  forgetAssets,
} from '../lib/browse-session.js';
import { fromDrop } from '../lib/folder-upload.js';
import { useUploads } from '../lib/upload-manager.js';
import { FilterSidebar } from '../components/FilterSidebar.js';
import { ReindexDialog } from '../components/ReindexDialog.js';
import { MoveDialog, type MoveResult } from '../components/MoveDialog.js';
import { useSession, TIER } from '../App.js';

const SORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'captured_at', label: 'Date captured' },
  { value: 'created_at', label: 'Date uploaded' },
  { value: 'filename', label: 'Name' },
  { value: 'size_bytes', label: 'File size' },
  { value: 'duration_s', label: 'Duration' },
  { value: 'width', label: 'Resolution' },
  { value: 'camera_model', label: 'Camera' },
  { value: 'iso', label: 'ISO' },
  { value: 'f_number', label: 'Aperture' },
  { value: 'focal_length', label: 'Focal length' },
];

const KIND_ICON: Record<string, string> = {
  video: '▶',
  image: '▣',
  audio: '♪',
  other: '◻',
};

export function BrowsePage(): ReactNode {
  const { folderId } = useParams<{ folderId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { me } = useSession();

  const [assets, setAssets] = useState<Asset[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<Folder[]>([]);
  const [facets, setFacets] = useState<Facets | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchMode, setSearchMode] = useState<string>('browse');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reindexing, setReindexing] = useState<
    { assetIds: string[] } | { folderId: string; folderName: string } | null
  >(null);

  const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '');

  // The URL is the single source of truth for filters, so a filtered view can
  // be pasted into Discord and opens the same way for whoever clicks it.
  const filters = useMemo((): SearchParams => {
    const out: SearchParams = {};
    for (const [key, value] of searchParams.entries()) {
      if (key === 'tags') {
        const existing = out.tags;
        out.tags = Array.isArray(existing) ? [...existing, value] : existing ? [String(existing), value] : [value];
      } else {
        out[key] = value;
      }
    }
    return out;
  }, [searchParams]);

  const sort = (searchParams.get('sort') as string) || 'captured_at';
  const order = (searchParams.get('order') as string) || 'desc';

  const updateParam = useCallback(
    (key: string, value: string | string[] | null): void => {
      const next = new URLSearchParams(searchParams);
      next.delete(key);
      if (Array.isArray(value)) {
        for (const v of value) next.append(key, v);
      } else if (value !== null && value !== '') {
        next.set(key, value);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // --- data loading -------------------------------------------------------

  const load = useCallback(
    async (append: boolean, afterCursor: string | null): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.listAssets({
          ...filters,
          folderId: folderId ?? null,
          cursor: afterCursor ?? undefined,
          limit: 60,
        });
        setAssets((prev) => (append ? [...prev, ...result.assets] : result.assets));
        setCursor(result.nextCursor);
        setHasMore(result.hasMore);
        setSearchMode(result.searchMode);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load media');
      } finally {
        setLoading(false);
      }
    },
    [filters, folderId],
  );

  /**
   * Identifies this view, so a snapshot is only reused for the same sequence.
   * Changing a filter, the sort or the search text makes a different one.
   */
  const viewKey = useMemo(
    () => browseKey(folderId, searchParams.toString()),
    [folderId, searchParams],
  );

  const mainRef = useRef<HTMLDivElement>(null);
  // Read on unmount, when the element may already be detached and its
  // scrollTop reads zero. Tracking it as it happens avoids depending on that.
  const scrollYRef = useRef(0);
  const [pendingScroll, setPendingScroll] = useState<
    { y: number; assetId: string | null } | null
  >(null);
  const [restoredFocus, setRestoredFocus] = useState<string | null>(null);

  useEffect(() => {
    const saved = readBrowseSnapshot(viewKey);
    if (saved) {
      // Everything that was loaded, not the first sixty of it. Someone who
      // scrolled a long way and opened a photo gets their place back rather
      // than the top of the grid.
      setAssets(saved.assets);
      setCursor(saved.cursor);
      setHasMore(saved.hasMore);
      setSearchMode(saved.searchMode);
      setSelected(new Set());
      setLoading(false);
      setRestoredFocus(saved.focusAssetId);
      setPendingScroll({ y: saved.scrollY, assetId: saved.focusAssetId });
      return;
    }

    setAssets([]);
    setSelected(new Set());
    setRestoredFocus(null);
    void load(false, null);
  }, [load, viewKey]);

  /**
   * Puts the grid back where it was.
   *
   * Before paint rather than after, so the restored position is the first thing
   * drawn instead of a flash of the top of the list. Scrolling the card itself
   * into view is preferred over the raw offset: it lands correctly even if the
   * column count changed because the window was resized in between.
   */
  useLayoutEffect(() => {
    if (!pendingScroll) return;
    const container = mainRef.current;
    if (!container) return;

    const card = pendingScroll.assetId
      ? container.querySelector(`[data-asset-id="${pendingScroll.assetId}"]`)
      : null;

    if (card) card.scrollIntoView({ block: 'center' });
    else container.scrollTop = pendingScroll.y;

    scrollYRef.current = container.scrollTop;
    setPendingScroll(null);
  }, [pendingScroll, assets]);

  // The highlight is only there to catch the eye on arrival; leaving it on
  // would read as a selection.
  useEffect(() => {
    if (!restoredFocus) return;
    const timer = setTimeout(() => setRestoredFocus(null), 1600);
    return () => clearTimeout(timer);
  }, [restoredFocus]);

  /**
   * Hands the grid to the viewer on the way out.
   *
   * Written from refs because this runs during unmount, when the state closed
   * over by the effect that created it is a render behind.
   */
  const snapshotRef = useRef({ viewKey, assets, cursor, hasMore, searchMode });
  snapshotRef.current = { viewKey, assets, cursor, hasMore, searchMode };

  useEffect(
    () => () => {
      const current = snapshotRef.current;
      if (current.assets.length === 0) return;
      saveBrowseSnapshot({
        key: current.viewKey,
        assets: current.assets,
        cursor: current.cursor,
        hasMore: current.hasMore,
        searchMode: current.searchMode,
        scrollY: scrollYRef.current,
        focusAssetId: null,
      });
    },
    [],
  );

  useEffect(() => {
    void api
      .listFolders({ parentId: folderId ?? null })
      .then((r) => setFolders(r.folders))
      .catch(() => setFolders([]));

    void api
      .facets(folderId ?? null)
      .then(setFacets)
      .catch(() => setFacets(null));

    if (folderId) {
      void api
        .getFolder(folderId)
        .then((r) => setBreadcrumbs([...r.ancestors, r.folder]))
        .catch(() => setBreadcrumbs([]));
    } else {
      setBreadcrumbs([]);
    }
  }, [folderId]);

  // --- infinite scroll ----------------------------------------------------

  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && cursor) void load(true, cursor);
      },
      // Start fetching before the sentinel is visible so scrolling stays smooth.
      { rootMargin: '600px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, cursor, load]);

  // --- selection ----------------------------------------------------------

  /**
   * Selects everything the current view matches, not just what has scrolled in.
   *
   * The grid loads sixty at a time, so "select all" used to mean "select the
   * sixty on screen" -- useless for putting away a few thousand files that were
   * uploaded to the top level. This pages through the rest first, in the
   * largest pages the server allows, and selects the lot. The ids are still
   * explicit, so what gets moved is exactly what the count said.
   *
   * Capped, because a selection is held in memory and posted whole. Past the
   * cap the first chunk is selected and the person is told to go again.
   */
  const MAX_SELECT = 5000;
  const [selectingAll, setSelectingAll] = useState<number | null>(null);

  const selectAllMatching = async (): Promise<void> => {
    // The view can change underneath a long load -- a filter clicked, a folder
    // opened. Pages for the old view must not be appended to the new one.
    const startKey = snapshotRef.current.viewKey;
    let all = assets;
    let next = cursor;
    let more = hasMore;

    // Pauses infinite scroll, which would otherwise append the same pages in
    // parallel and duplicate them.
    setLoading(true);
    setSelectingAll(all.length);
    try {
      while (more && next && all.length < MAX_SELECT) {
        const page = await api.listAssets({
          ...filters,
          folderId: folderId ?? null,
          cursor: next,
          limit: 200,
        });
        if (snapshotRef.current.viewKey !== startKey) return;
        all = [...all, ...page.assets];
        next = page.nextCursor;
        more = page.hasMore;
        setSelectingAll(all.length);
      }

      setAssets(all);
      setCursor(next);
      setHasMore(more);
      setSelected(new Set(all.slice(0, MAX_SELECT).map((a) => a.id)));
      setError(
        more || all.length > MAX_SELECT
          ? `Selected the first ${MAX_SELECT.toLocaleString()}. Move or download those, ` +
              'then select all again for the rest.'
          : null,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load everything to select it');
    } finally {
      setSelectingAll(null);
      setLoading(false);
    }
  };

  // --- moving -------------------------------------------------------------

  const [moveOpen, setMoveOpen] = useState(false);
  const [moveNotice, setMoveNotice] = useState<{ text: string; folderId: string } | null>(null);

  const onMoved = (result: MoveResult): void => {
    setMoveOpen(false);
    const ids = [...selected];
    const destination = result.folderId || null;

    // Whether the moved files still belong in what is on screen. In "All media"
    // they do wherever they went; in a folder, or in the not-in-a-folder view,
    // anything that went elsewhere has left.
    const unfiledView = !folderId && searchParams.get('unfiled') === 'true';
    const leftView =
      (folderId !== undefined && destination !== folderId) ||
      (unfiledView && destination !== null);

    if (leftView) {
      if (result.denied === 0 && result.notFound === 0) {
        // Dropped in place rather than refetched, so the grid does not jump back
        // to the top halfway through tidying a long folder.
        const gone = new Set(ids);
        setAssets((prev) => prev.filter((a) => !gone.has(a.id)));
        forgetAssets(ids);
      } else {
        // Some stayed behind, and the response names them rather than listing
        // their ids, so the honest thing is to ask the server what is left.
        void load(false, null);
      }
    }
    setSelected(new Set());

    // Folder cards show file counts, which have just changed.
    void api
      .listFolders({ parentId: folderId ?? null })
      .then((r) => setFolders(r.folders))
      .catch(() => {});

    const parts = [`Moved ${result.moved.toLocaleString()} file${result.moved === 1 ? '' : 's'}.`];
    if (result.unchanged > 0) parts.push(`${result.unchanged} were already there.`);
    if (result.denied > 0) {
      parts.push(
        `${result.denied} could not be moved out of where they are` +
          (result.deniedExamples.length > 0 ? ` (${result.deniedExamples.join(', ')}…)` : '') +
          '.',
      );
    }
    if (result.notFound > 0) parts.push(`${result.notFound} had been deleted.`);
    setMoveNotice({ text: parts.join(' '), folderId: result.folderId });
  };

  /** Matches the server's cap, so an oversized selection is refused in words. */
  const MAX_DOWNLOAD = 1000;

  const selectedBytes = useMemo(
    () => assets.reduce((total, a) => (selected.has(a.id) ? total + a.size_bytes : total), 0),
    [assets, selected],
  );

  /**
   * Downloads the selection as one zip.
   *
   * The server assembles it on the fly, so this is a navigation rather than a
   * fetch: holding a multi-gigabyte archive in a Blob to hand it to the browser
   * would run the tab out of memory long before the download started. Assigning
   * to location with an attachment response begins the download without
   * unloading the page, so the grid, the scroll position and the selection all
   * survive it.
   */
  const downloadSelected = async (): Promise<void> => {
    const ids = [...selected];
    if (ids.length === 0) return;

    if (ids.length > MAX_DOWNLOAD) {
      setError(
        `That is ${ids.length} files. Zip downloads are capped at ${MAX_DOWNLOAD} — ` +
          'select fewer, or download a folder at a time.',
      );
      return;
    }

    setDownloading(true);
    try {
      const prepared = await api.prepareDownload(ids);
      window.location.href = prepared.url;

      const skipped = prepared.denied + prepared.missing + prepared.notFound;
      setError(
        skipped > 0
          ? `Downloading ${prepared.files} of ${ids.length}. Skipped ${skipped}: ` +
              `${prepared.denied} you cannot download, ${prepared.missing} missing from ` +
              `storage, ${prepared.notFound} already deleted.`
          : null,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not prepare that download');
    } finally {
      setDownloading(false);
    }
  };

  const deleteSelected = async (): Promise<void> => {
    const ids = [...selected];
    if (ids.length === 0) return;

    const plural = ids.length === 1 ? '' : 's';
    const message = [
      `Delete ${ids.length} item${plural}?`,
      '',
      'They are removed from the archive but recoverable by an admin.',
    ].join(String.fromCharCode(10));

    if (!confirm(message)) return;

    setDeleting(true);
    try {
      const result = await api.bulkDeleteAssets(ids);

      // Drop the deleted rows locally rather than refetching, so the grid does
      // not jump back to the top of a long scroll.
      const deletedSet = new Set(ids);
      setAssets((prev) => prev.filter((a) => !deletedSet.has(a.id)));
      // The viewer walks this list too, and an arrow key landing on something
      // that was just deleted opens a page that only says "not found".
      forgetAssets(ids);
      setSelected(new Set());

      if (result.denied > 0) {
        setError(
          `Deleted ${result.deleted}. Skipped ${result.denied} you do not have ` +
            `permission to delete` +
            (result.deniedExamples.length > 0
              ? ` (${result.deniedExamples.join(', ')}${result.denied > result.deniedExamples.length ? ', …' : ''})`
              : '') +
            '. Deleting other people’s uploads needs lead permissions.',
        );
      } else {
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Deleting a folder takes its contents with it, so the API refuses a
   * non-empty folder until `force` is passed. That 409 carries the counts,
   * which is what makes the second prompt specific rather than generic.
   */
  const deleteFolder = async (folder: Folder): Promise<void> => {
    if (!confirm(`Delete the folder "${folder.name}"?`)) return;

    try {
      await api.deleteFolder(folder.id, false);
      setFolders((prev) => prev.filter((f) => f.id !== folder.id));
      setError(null);
      return;
    } catch (err) {
      const details = (err as { details?: { assets?: number; folders?: number } }).details;
      if (!details) {
        setError(err instanceof Error ? err.message : 'Could not delete that folder');
        return;
      }

      const parts: string[] = [];
      if (details.assets) parts.push(`${details.assets} file(s)`);
      if (details.folders) parts.push(`${details.folders} subfolder(s)`);

      if (
        !confirm(
          `"${folder.name}" contains ${parts.join(' and ')}.

` +
            'Delete it and everything inside? Files can be restored from ' +
            'Admin → Trash afterwards.',
        )
      ) {
        return;
      }

      try {
        const result = await api.deleteFolder(folder.id, true);
        setFolders((prev) => prev.filter((f) => f.id !== folder.id));
        setError(
          result.deletedAssets > 0
            ? `Deleted "${folder.name}" and ${result.deletedAssets} file(s). ` +
              'Recoverable from Admin → Trash.'
            : null,
        );
      } catch (forceErr) {
        setError(forceErr instanceof Error ? forceErr.message : 'Could not delete that folder');
      }
    }
  };

  /**
   * Once anything is selected, the grid switches to selection mode: clicking a
   * card toggles it rather than opening it. Picking the first item still needs
   * the checkbox, so an ordinary click-to-open is never ambiguous.
   */
  const selectionMode = selected.size > 0;

  const toggleSelect = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (!selectionMode) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setSelected(new Set());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectionMode]);

  // --- adding to the folder being viewed ------------------------------------

  // Same tier as the Upload tab. Dropping a folder tree here creates its
  // folders too, through the same /folders/ensure path the upload page uses.
  const canUpload = (me?.tier ?? 0) >= TIER.member;

  const { enqueue } = useUploads();
  const [dropping, setDropping] = useState(false);
  const [dropNotice, setDropNotice] = useState<string | null>(null);
  // dragenter and dragleave fire for every child the pointer crosses, so a
  // plain boolean flickers off whenever it passes over a card. Counting
  // enters against leaves tracks whether it is still anywhere over the page.
  const dragDepth = useRef(0);

  /**
   * A drop that misses the grid must do nothing.
   *
   * The browser's default for a file dropped on a page is to navigate the tab
   * to it, which unloads the app -- and the upload manager with it, abandoning
   * every transfer in flight. Inviting people to drop onto this page makes a
   * near-miss onto the sidebar likely, so the default is cancelled window-wide
   * while it is mounted. The grid's own handlers still receive the drop.
   */
  useEffect(() => {
    if (!canUpload) return;
    const swallow = (event: DragEvent): void => {
      if (event.dataTransfer && Array.from(event.dataTransfer.types).includes('Files')) {
        event.preventDefault();
      }
    };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, [canUpload]);

  /** Only a drag carrying files is ours; dragging a card's link around is not. */
  const carriesFiles = (event: React.DragEvent): boolean =>
    Array.from(event.dataTransfer.types).includes('Files');

  const currentFolderName = folderId ? (breadcrumbs.at(-1)?.name ?? 'this folder') : 'the top level';

  /**
   * Uploads straight into the folder on screen.
   *
   * This is the answer to "how do I put a photo in a folder three levels
   * down": go there and drop it. The upload manager sits above the router, so
   * the transfer carries on if you browse away, and the header shows progress
   * as it does for anything started from the Upload tab.
   */
  const onDropFiles = (event: React.DragEvent): void => {
    event.preventDefault();
    dragDepth.current = 0;
    setDropping(false);
    // The DataTransfer is neutered once the handler returns, so it is read
    // synchronously here and walked afterwards.
    const transfer = event.dataTransfer;
    const destination = folderId ?? null;
    const label = currentFolderName;
    setDropNotice(`Reading what you dropped…`);
    void fromDrop(transfer)
      .then((candidates) => {
        if (candidates.length === 0) {
          setDropNotice(null);
          return;
        }
        enqueue(candidates, destination);
        setDropNotice(
          `Uploading ${candidates.length} file${candidates.length === 1 ? '' : 's'} to ${label}. ` +
            'They appear here once processed — refresh to see them.',
        );
      })
      .catch((err: unknown) => {
        setDropNotice(null);
        setError(err instanceof Error ? err.message : 'Could not read what was dropped');
      });
  };

  /**
   * Creates a folder inside the one on screen.
   *
   * Available to anyone who can upload, matching folder:create on the server.
   * The new folder is added to the list in place rather than refetching, so the
   * grid below does not reload.
   */
  const createSubfolder = async (): Promise<void> => {
    const name = prompt(`New folder inside ${currentFolderName}:`)?.trim();
    if (!name) return;
    try {
      const { folder } = await api.createFolder({ name, parentId: folderId ?? null });
      setFolders((prev) =>
        [...prev, { ...(folder as Folder), asset_count: 0, total_bytes: 0, child_count: 0 }].sort(
          (a, b) => a.name.localeCompare(b.name),
        ),
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create that folder');
    }
  };

  const submitSearch = (event: React.FormEvent): void => {
    event.preventDefault();
    updateParam('q', searchInput.trim() || null);
  };

  const canEdit = (me?.tier ?? 0) >= TIER.editor;
  // Matches asset:move on the server.
  const canMove = (me?.tier ?? 0) >= TIER.editor;
  // Editors can delete their own uploads; the server enforces per-asset
  // ownership and reports anything it skipped.
  const canDelete = (me?.tier ?? 0) >= TIER.editor;
  // Folders are a shared structure, so removing one is lead-tier even
  // though deleting your own uploads is not.
  const canManageFolders = (me?.tier ?? 0) >= TIER.lead;
  // Reindexing is queued work that can occupy the GPU for hours, so it sits
  // at the same tier as managing folders rather than with everyday editing.
  const canReindex = (me?.tier ?? 0) >= TIER.lead;

  return (
    <div className="body">
      <FilterSidebar
        facets={facets}
        params={searchParams}
        onChange={updateParam}
        onClear={() => setSearchParams(new URLSearchParams(), { replace: true })}
      />

      <div
        className={`main${dropping ? ' drop-target' : ''}`}
        ref={mainRef}
        onScroll={(e) => {
          scrollYRef.current = e.currentTarget.scrollTop;
        }}
        onDragEnter={(e) => {
          if (!canUpload || !carriesFiles(e)) return;
          e.preventDefault();
          dragDepth.current += 1;
          setDropping(true);
        }}
        onDragOver={(e) => {
          // Without preventDefault on dragover the browser refuses the drop
          // and opens the file in the tab instead, replacing the app.
          if (!canUpload || !carriesFiles(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={(e) => {
          if (!canUpload || !carriesFiles(e)) return;
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDropping(false);
        }}
        onDrop={(e) => {
          if (!canUpload || !carriesFiles(e)) return;
          onDropFiles(e);
        }}
      >
        {dropping && (
          <div className="drop-overlay" aria-hidden="true">
            <div>
              Drop to upload to <strong>{currentFolderName}</strong>
              <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>
                Folders keep their structure
              </div>
            </div>
          </div>
        )}
        {/* breadcrumbs */}
        <div className="flex" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
          <Link to="/" className="btn sm">
            All media
          </Link>
          {!folderId && (
            // "All media" is everything, filed or not. This narrows it to the
            // files that are in no folder, which is what needs putting away.
            <label
              className="flex"
              style={{ alignItems: 'center', gap: 6, fontSize: 12, marginLeft: 6 }}
            >
              <input
                type="checkbox"
                checked={searchParams.get('unfiled') === 'true'}
                onChange={(e) => updateParam('unfiled', e.target.checked ? 'true' : null)}
              />
              Only files not in a folder
            </label>
          )}
          {breadcrumbs.map((folder) => (
            <span key={folder.id} className="flex">
              <span style={{ color: 'var(--text-dim)' }}>/</span>
              <Link to={`/folder/${folder.id}`} className="btn sm">
                {folder.name}
              </Link>
            </span>
          ))}
          <div className="spacer" />
          {canUpload && (
            <Link
              to={folderId ? `/upload?folder=${folderId}` : '/upload'}
              className="btn sm"
              title={`Upload into ${currentFolderName} — or just drop files onto this page`}
            >
              Upload here
            </Link>
          )}
          {canUpload && (
            <button
              className="btn sm"
              onClick={() => void createSubfolder()}
              title={`Create a folder inside ${currentFolderName}`}
            >
              New folder
            </button>
          )}
          {canReindex && folderId && breadcrumbs.length > 0 && (
            <>
              <button
                className="btn sm"
                onClick={() =>
                  setReindexing({
                    folderId,
                    folderName: breadcrumbs.at(-1)?.name ?? 'this folder',
                  })
                }
                title="Re-run processing over this folder and its subfolders"
              >
                Reanalyze folder
              </button>
            </>
          )}
        </div>

        {/* toolbar */}
        <form className="toolbar" onSubmit={submitSearch}>
          <div className="search-bar">
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search names, transcripts, or describe what you're looking for…"
              aria-label="Search"
            />
          </div>
          <button className="btn" type="submit">
            Search
          </button>

          <select value={sort} onChange={(e) => updateParam('sort', e.target.value)}>
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                Sort: {option.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="btn"
            onClick={() => updateParam('order', order === 'desc' ? 'asc' : 'desc')}
            title={order === 'desc' ? 'Descending' : 'Ascending'}
          >
            {order === 'desc' ? '↓' : '↑'}
          </button>

          {assets.length > 0 && selected.size === 0 && (
            <button
              type="button"
              className="btn sm"
              disabled={selectingAll !== null}
              onClick={() => void selectAllMatching()}
              title={hasMore ? 'Loads everything this view matches, then selects it' : undefined}
            >
              {selectingAll !== null
                ? `Loading ${selectingAll.toLocaleString()}…`
                : hasMore
                  ? 'Select all'
                  : `Select all (${assets.length})`}
            </button>
          )}

          {selected.size > 0 && (
            <>
              <span style={{ color: 'var(--text-dim)' }}>
                {selected.size} selected
                <span style={{ fontSize: 11 }}> · click to add · Esc to cancel</span>
              </span>
              {(selected.size < assets.length || hasMore) && (
                <button
                  type="button"
                  className="btn sm"
                  disabled={selectingAll !== null}
                  onClick={() => void selectAllMatching()}
                >
                  {selectingAll !== null
                    ? `Loading ${selectingAll.toLocaleString()}…`
                    : hasMore
                      ? 'Select all'
                      : `Select all (${assets.length})`}
                </button>
              )}
              {canMove && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => setMoveOpen(true)}
                  title="Move the selected files into a folder"
                >
                  Move to…
                </button>
              )}
              <button
                type="button"
                className="btn primary"
                disabled={downloading}
                onClick={() => void downloadSelected()}
                title="Download the originals as one zip"
              >
                {downloading
                  ? 'Preparing…'
                  : `Download ${selected.size}${selectedBytes > 0 ? ` (${formatBytes(selectedBytes)})` : ''}`}
              </button>
              {canEdit && (
                <button
                  type="button"
                  className="btn"
                  onClick={() =>
                    navigate(`/edit?assets=${[...selected].join(',')}`)
                  }
                >
                  Auto-edit these
                </button>
              )}
              {canReindex && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => setReindexing({ assetIds: [...selected] })}
                  title="Re-run AI analysis, metadata or transcription"
                >
                  Reanalyze
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  className="btn danger"
                  disabled={deleting}
                  onClick={() => void deleteSelected()}
                >
                  {deleting ? 'Deleting…' : `Delete ${selected.size}`}
                </button>
              )}
              <button type="button" className="btn sm" onClick={() => setSelected(new Set())}>
                Clear
              </button>
            </>
          )}
        </form>

        {searchMode === 'semantic' && (
          <div className="notice">
            Showing visually similar results ranked by AI image understanding, not keyword
            matches.
          </div>
        )}
        {error && <div className="notice error">{error}</div>}
        {moveNotice && (
          <div className="notice success">
            {moveNotice.text}{' '}
            <Link
              to={moveNotice.folderId ? `/folder/${moveNotice.folderId}` : '/'}
              style={{ textDecoration: 'underline' }}
            >
              Open the folder
            </Link>
            <button
              className="btn sm"
              style={{ marginLeft: 8 }}
              onClick={() => setMoveNotice(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}
        {dropNotice && (
          <div className="notice success">
            {dropNotice}{' '}
            <Link to="/upload" style={{ textDecoration: 'underline' }}>
              View progress
            </Link>
            <button
              className="btn sm"
              style={{ marginLeft: 8 }}
              onClick={() => setDropNotice(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* subfolders */}
        {folders.length > 0 && (
          <div className="grid" style={{ marginBottom: 20 }}>
            {folders.map((folder) => (
              <div key={folder.id} className="card">
                <Link to={`/folder/${folder.id}`}>
                  <div className="thumb placeholder" style={{ aspectRatio: '16 / 6' }}>
                    📁
                  </div>
                  <div className="meta">
                    <div className="name">{folder.name}</div>
                    <div className="sub">
                      <span>{folder.asset_count ?? 0} files</span>
                      <span>{formatBytes(folder.total_bytes ?? 0)}</span>
                    </div>
                  </div>
                </Link>
                {canManageFolders && (
                  <button
                    className="btn sm danger"
                    style={{ position: 'absolute', top: 6, right: 6 }}
                    title={`Delete "${folder.name}"`}
                    onClick={(e) => {
                      e.preventDefault();
                      void deleteFolder(folder);
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* assets */}
        {assets.length === 0 && !loading ? (
          <div className="empty">
            <p>Nothing here yet.</p>
            {searchParams.toString() && (
              <button
                className="btn"
                onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid">
            {assets.map((asset) => (
              <div
                key={asset.id}
                data-asset-id={asset.id}
                className={
                  `card${selected.has(asset.id) ? ' selected' : ''}` +
                  (restoredFocus === asset.id ? ' returned' : '')
                }
              >
                <input
                  type="checkbox"
                  className="select-box"
                  checked={selected.has(asset.id)}
                  onChange={() => toggleSelect(asset.id)}
                  aria-label={`Select ${asset.filename}`}
                />
                <Link
                  to={`/asset/${asset.id}`}
                  onClick={(e) => {
                    // preventDefault rather than swapping the element for a
                    // div: the anchor stays an anchor, so middle-click and
                    // "open in new tab" still reach the asset even mid-selection.
                    if (selectionMode) {
                      e.preventDefault();
                      toggleSelect(asset.id);
                    }
                  }}
                  title={selectionMode ? 'Click to select · Esc to cancel' : asset.filename}
                >
                  {asset.has_thumb ? (
                    <img
                      className="thumb"
                      src={assetUrls.thumb(asset.id)}
                      alt={asset.filename}
                      loading="lazy"
                    />
                  ) : (
                    <div className="thumb placeholder">{KIND_ICON[asset.kind] ?? '◻'}</div>
                  )}
                  {asset.kind === 'video' && asset.duration_s !== null && (
                    <span className="badge">{formatDuration(asset.duration_s)}</span>
                  )}
                  <div className="meta">
                    <div className="name" title={asset.filename}>
                      {asset.filename}
                    </div>
                    <div className="sub">
                      <span>
                        {asset.captured_at
                          ? new Date(asset.captured_at).toLocaleDateString()
                          : '—'}
                      </span>
                      <span>{formatBytes(asset.size_bytes)}</span>
                      {asset.width && <span>{asset.width}px</span>}
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}

        {moveOpen && (
          <MoveDialog
            assetIds={[...selected]}
            startFolderId={folderId ?? ''}
            canCreate={canUpload}
            onClose={() => setMoveOpen(false)}
            onMoved={onMoved}
          />
        )}

        {reindexing && (
          <ReindexDialog
            target={reindexing}
            onClose={() => setReindexing(null)}
          />
        )}

        {loading && <div className="empty">Loading…</div>}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>
    </div>
  );
}
